import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";

import keys from "./keys";
import { Area, Post } from "./types";
import { useApi } from "../client";

const doesModuleAlreadyExistsSchema = z.object({
  areaId: z.string().min(2, "Area ID is required"),
  moduleAddress: z.string().min(2, "Module address is required"),
});

export const useArea = ({ id }: { id: string }) => {
  const api = useApi();
  const queryKey = keys.one(id);

  return useQuery({
    queryKey: queryKey,
    queryFn: async (): Promise<Area> => {
      const area = await api.get(`areas/${id}`);

      return await area.json();
    },
  });
};

export const useAreas = () => {
  const api = useApi();

  return useQuery({
    queryKey: keys.all(),
    queryFn: async (): Promise<Area[]> => {
      const areas = await api.get(`areas`);

      return await areas.json();
    },
  });
};

export const useAreaPostSuspense = ({ address }: { address: string }) => {
  const api = useApi();

  return useSuspenseQuery({
    queryKey: keys.one(address),
    queryFn: async (): Promise<Post> => {
      const post = await api.get(`areas/post/${address}`);
      return await post.json();
    },
  });
};

export const useDoesModuleAlreadyExists = ({
  areaId,
  moduleAddress,
}: {
  areaId?: string;
  moduleAddress?: string;
}) => {
  const api = useApi();

  const validated = doesModuleAlreadyExistsSchema.safeParse({
    areaId,
    moduleAddress,
  });

  return useQuery({
    queryKey: ["areas", areaId, moduleAddress],
    enabled: validated.success,
    queryFn: async (): Promise<boolean> => {
      const result = await api.get(
        `areas/does-module-already-exists/${areaId}/${moduleAddress}`
      );
      const json = await result.json<{ moduleAddress: boolean }>();
      return json.moduleAddress;
    },
  });
};
